import { useState, useRef, useEffect, useCallback } from 'react';
import { XMarkIcon, UserIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { contactApi, matterApi } from '../../config/api';
import type { Contact, Matter } from '../../config/api';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ClientResult {
  id: string;
  name: string;
  type?: string;
}

export interface MatterResult {
  id: string;
  matter_id?: string;
  matter_number?: string;
  title?: string;
  status?: string;
}

export interface ClientMatterSelection {
  clientId: string;
  clientName: string;
  matterId: string;
  matterNumber: string;
  matterName: string;
}

interface ClientMatterPickerProps {
  /** Called when a matter is selected (client+matter fully resolved) */
  onSelect: (selection: ClientMatterSelection) => void;
  /** Called when picker is dismissed without selection */
  onCancel: () => void;
  /** Existing linked matter to display (if any) */
  currentMatterId?: string | null;
  currentMatterLabel?: string;
  /**
   * Visual variant.
   * - `em`: uses EdgeMail theme tokens (`--em-*`) so the picker tracks the active EdgeMail theme (light/dark/high-contrast, accent color, etc.). Use inside EdgeMail.
   * - `dark` / `light`: hardcoded palettes. Used by non-EdgeMail call sites (e.g. billing) where the EdgeMail tokens are not loaded.
   */
  variant?: 'em' | 'dark' | 'light';
  /** When true, skip matter selection — return after client is picked */
  clientOnly?: boolean;
  /** Accent tone for focus/pill styling */
  accentTone?: 'accent' | 'warning';
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function ClientMatterPicker({
  onSelect,
  onCancel,
  variant = 'dark',
  clientOnly = false,
  accentTone = 'accent',
}: ClientMatterPickerProps) {
  // Stage: 'client' → searching clients, 'matter' → selecting matter
  const [stage, setStage] = useState<'client' | 'matter'>('client');

  // Client state
  const [clientQuery, setClientQuery] = useState('');
  const [clients, setClients] = useState<ClientResult[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientResult | null>(null);

  // Matter state
  const [matters, setMatters] = useState<MatterResult[]>([]);
  const [matterLoading, setMatterLoading] = useState(false);

  const clientInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Style tokens based on variant
  let bg: string;
  let border: string;
  let inputBg: string;
  let itemHover: string;
  let textPrimary: string;
  let textSecondary: string;
  let textMuted: string;
  let separatorBorder: string;
  if (variant === 'em') {
    // Use EdgeMail theme tokens — light/dark/accent all resolved by the EdgeMail ThemeProvider.
    bg = 'bg-[var(--em-surface-card)]';
    border = 'border-[var(--em-border-primary)]';
    inputBg =
      'bg-[var(--em-surface-app)] border-[var(--em-border-primary)] text-[var(--em-text-primary)] placeholder-[var(--em-text-muted)]';
    itemHover = 'hover:bg-[var(--em-state-hover)]';
    textPrimary = 'text-[var(--em-text-primary)]';
    textSecondary = 'text-[var(--em-text-secondary)]';
    textMuted = 'text-[var(--em-text-tertiary)]';
    separatorBorder = 'border-[var(--em-border-subtle)]';
  } else if (variant === 'dark') {
    bg = 'bg-gray-900';
    border = 'border-gray-700';
    inputBg = 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500';
    itemHover = 'hover:bg-white/[0.06]';
    textPrimary = 'text-gray-200';
    textSecondary = 'text-gray-400';
    textMuted = 'text-gray-500';
    separatorBorder = 'border-gray-800';
  } else {
    // light
    bg = 'bg-white';
    border = 'border-gray-300';
    inputBg = 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400';
    itemHover = 'hover:bg-gray-100';
    textPrimary = 'text-gray-900';
    textSecondary = 'text-gray-500';
    textMuted = 'text-gray-400';
    separatorBorder = 'border-gray-200';
  }
  const pillBg = 'bg-[var(--picker-accent-light)] text-[var(--picker-accent)]';
  const accentVars =
    accentTone === 'warning'
      ? {
          color: 'var(--em-status-warning-text, var(--brand-accent-primary))',
          light: 'var(--em-status-warning-bg, var(--brand-accent-primary-soft))',
        }
      : {
          color: 'var(--em-accent-primary, var(--brand-accent-primary))',
          light: 'var(--em-accent-primary-light, var(--brand-accent-primary-soft))',
        };

  // Focus input on mount
  useEffect(() => {
    clientInputRef.current?.focus();
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onCancel]);

  // ── Client search (debounced) ───────────────────────────────────────────
  useEffect(() => {
    if (stage !== 'client' || !clientQuery.trim()) {
      setClients([]);
      return;
    }
    const timer = setTimeout(async () => {
      setClientLoading(true);
      try {
        const response: any = await contactApi.search(clientQuery);
        const raw: any[] = response.clients || response.data || [];
        const list: ClientResult[] = raw.map((c: any) => ({
          id: c.id || c.client_id,
          name: c.name || c.client_name,
          type: c.type || c.client_type,
        }));
        setClients(list);
      } catch {
        setClients([]);
      } finally {
        setClientLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientQuery, stage]);

  // ── Load matters when client selected ───────────────────────────────────
  useEffect(() => {
    if (!selectedClient) return;
    setMatterLoading(true);
    (async () => {
      try {
        const response: any = await matterApi.getAll({ client_id: selectedClient.id });
        const raw: any[] = Array.isArray(response) ? response : (response.data || response.matters || []);
        const list: MatterResult[] = raw.map((m: any) => ({
          id: m.id || m.matter_id,
          matter_id: m.matter_id || m.id,
          matter_number: m.matter_number || m.matterNumber || '',
          title: m.title || m.matter_title || m.name || '',
          status: m.status || m.stage || '',
        }));
        setMatters(list);
      } catch {
        setMatters([]);
      } finally {
        setMatterLoading(false);
      }
    })();
  }, [selectedClient]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleClientSelect = useCallback((client: ClientResult) => {
    if (clientOnly) {
      // Skip matter selection — return immediately with client info
      onSelect({ clientId: client.id, clientName: client.name, matterId: '', matterNumber: '', matterName: '' });
      return;
    }
    setSelectedClient(client);
    setStage('matter');
    setClientQuery('');
    setClients([]);
  }, [clientOnly, onSelect]);

  const handleClientClear = useCallback(() => {
    setSelectedClient(null);
    setStage('client');
    setMatters([]);
    setClientQuery('');
    setTimeout(() => clientInputRef.current?.focus(), 50);
  }, []);

  const handleMatterSelect = useCallback((matter: MatterResult) => {
    if (!selectedClient) return;
    onSelect({
      clientId: selectedClient.id,
      clientName: selectedClient.name,
      matterId: matter.id,
      matterNumber: matter.matter_number || '',
      matterName: matter.title || '',
    });
  }, [selectedClient, onSelect]);

  return (
    <div
      ref={containerRef}
      className={`w-80 ${bg} border ${border} rounded-lg shadow-xl z-50 overflow-hidden`}
      style={{
        ['--picker-accent' as any]: accentVars.color,
        ['--picker-accent-light' as any]: accentVars.light,
      }}
    >
      {/* ── Stage 1: Client Search ──────────────────────────────── */}
      <div className="p-2">
        {selectedClient ? (
          <div className="flex items-center gap-2">
            <UserIcon className={`w-3.5 h-3.5 ${textSecondary} shrink-0`} />
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${pillBg} flex items-center gap-1`}>
              {selectedClient.name}
              <button onClick={handleClientClear} className="hover:opacity-70">
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <UserIcon className={`w-3.5 h-3.5 ${textSecondary} shrink-0`} />
            <input
              ref={clientInputRef}
              type="text"
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              placeholder="Search clients..."
              className={`flex-1 px-2 py-1.5 text-sm border rounded ${inputBg} outline-none focus:border-[var(--picker-accent)] focus:ring-1 focus:ring-[var(--picker-accent)]`}
            />
          </div>
        )}
      </div>

      {/* Client results */}
      {stage === 'client' && (
        <div className={`max-h-40 overflow-y-auto border-t ${separatorBorder}`}>
          {clientLoading && (
            <p className={`px-3 py-2 text-xs ${textMuted}`}>Searching...</p>
          )}
          {!clientLoading && clientQuery.trim() && clients.length === 0 && (
            <p className={`px-3 py-2 text-xs ${textMuted}`}>No clients found</p>
          )}
          {clients.map((client) => (
            <button
              key={client.id}
              onClick={() => handleClientSelect(client)}
              className={`w-full text-left px-3 py-2 text-sm ${textPrimary} ${itemHover} transition-colors`}
            >
              <span className="font-medium">{client.name}</span>
              {client.type && <span className={`ml-2 text-xs ${textSecondary}`}>({client.type})</span>}
            </button>
          ))}
        </div>
      )}

      {/* ── Stage 2: Matter Selection ───────────────────────────── */}
      {stage === 'matter' && (
        <>
          <div className={`border-t ${separatorBorder} p-2`}>
            <div className={`flex items-center gap-2 text-xs ${textSecondary}`}>
              <BriefcaseIcon className="w-3.5 h-3.5 shrink-0" />
              <span>Select a matter</span>
            </div>
          </div>
          <div className={`max-h-48 overflow-y-auto border-t ${separatorBorder}`}>
            {matterLoading && (
              <p className={`px-3 py-2 text-xs ${textMuted}`}>Loading matters...</p>
            )}
            {!matterLoading && matters.length === 0 && (
              <p className={`px-3 py-2 text-xs ${textMuted}`}>No matters for this client</p>
            )}
            {matters.map((matter) => (
              <button
                key={matter.id}
                onClick={() => handleMatterSelect(matter)}
                className={`w-full text-left px-3 py-2 text-sm ${textPrimary} ${itemHover} transition-colors`}
              >
                {matter.matter_number && (
                  <span className="font-medium">{matter.matter_number}: </span>
                )}
                <span>{matter.title}</span>
                {matter.status && (
                  <span className={`ml-2 text-xs ${textSecondary}`}>({matter.status})</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── Footer ──────────────────────────────────────────────── */}
      <div className={`p-1 border-t ${separatorBorder} flex justify-end`}>
        <button
          onClick={onCancel}
          className={`p-1 ${textMuted} hover:${textSecondary} rounded`}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
